import {
  type GroupRow,
  planGroupSave,
  settleGroupSaveStep
} from 'v5/core/resource-groups/groupsDraft';
import {
  type GroupsSaveProgress,
  GroupsSaveError,
  type ResourceGroupsMutationService
} from './ResourceGroupsMutationService';

/**
 * Writes a groups draft with the per-group writes the API has: archives, then
 * updates, then creates, then the order. Resumes from `progress`, so a retry
 * after a failure doesn't repeat what was written; a failure carries what was
 * written before it (`GroupsSaveError`).
 */
export async function saveResourceGroups(
  mutations: Pick<
    ResourceGroupsMutationService,
    'create' | 'update' | 'archive' | 'reorder'
  >,
  resourceTypeId: number,
  progress: GroupsSaveProgress,
  draft: ReadonlyArray<GroupRow>
): Promise<{ progress: GroupsSaveProgress; rows: GroupRow[] }> {
  let { saved, orderPending } = progress;
  let rows = [...draft];
  for (const step of planGroupSave(saved, rows, orderPending)) {
    try {
      let createdId: number | undefined;
      switch (step.type) {
        case 'archive':
          await mutations.archive(step.groupId);
          break;
        case 'update':
          await mutations.update(step.groupId, step.patch);
          break;
        case 'create': {
          const created = await mutations.create(resourceTypeId, {
            name: step.name,
            colour: step.colour,
            order: step.order
          });
          createdId = created.id;
          orderPending = true;
          rows = rows.map(row =>
            row.rowKey === step.rowKey ? { ...row, groupId: created.id } : row
          );
          break;
        }
        case 'reorder':
          await mutations.reorder(
            rows.flatMap(row => (row.groupId === null ? [] : [row.groupId]))
          );
          orderPending = false;
          break;
      }
      saved = settleGroupSaveStep(saved, step, { rows, createdId });
    } catch (cause) {
      throw new GroupsSaveError({ saved, orderPending }, rows, cause);
    }
  }
  return {
    progress: { saved, orderPending },
    // The names as written: the form shows what was saved.
    rows: rows.map(row => ({ ...row, name: row.name.trim() }))
  };
}
